'use client';

import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Bot, User, Database, Download, FileSpreadsheet, AlertCircle } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { ChatMessage as ChatMessageType, ToolCall, ExcelExportResult } from '@/types/chat';

const markdownComponents = {
  table: ({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
    <div className="table-wrapper">
      <table {...props}>{children}</table>
    </div>
  ),
};

interface ChatMessageProps {
  message: ChatMessageType;
}

/**
 * Displays a single chat message with expandable tool calls.
 *
 * User messages appear on the right, assistant messages on the left.
 * Assistant messages may include tool calls (SQL queries) that can be
 * expanded to show the query and result.
 */
export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const hasToolCalls = message.tool_calls.length > 0;

  // Avatar content: user initials or fallback icon
  const avatarContent = isUser ? (
    message.user_initials ? (
      <span className="text-xs font-semibold">{message.user_initials}</span>
    ) : (
      <User className="w-4 h-4" />
    )
  ) : (
    <Bot className="w-4 h-4" />
  );

  // User name label below the message
  const senderLabel = isUser ? message.user_name : 'Cortex';

  return (
    <div className="flex gap-3 mb-4">
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser ? 'bg-coral text-white' : 'bg-blue-1 text-white'
        )}
      >
        {avatarContent}
      </div>

      {/* Message Content */}
      <div className="flex flex-col max-w-[80%] items-start">
        {/* Sender name */}
        {senderLabel && <span className="text-xs text-text-placeholder mb-0.5 px-1">{senderLabel}</span>}

        {/* Message Bubble */}
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 shadow-sm',
            isUser ? 'bg-coral/10 text-text-primary rounded-bl-md' : 'bg-surface-raised text-text-primary rounded-bl-md'
          )}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
          ) : (
            <div className="chat-markdown">
              <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {message.content}
              </Markdown>
            </div>
          )}
        </div>

        {/* Tool Calls (for assistant messages) */}
        {!isUser && hasToolCalls && <ToolCallsSection toolCalls={message.tool_calls} />}

        {/* Timestamp */}
        <span className="text-xs text-text-placeholder mt-1 px-1">{formatTime(message.created_at)}</span>
      </div>
    </div>
  );
}

/**
 * Collapsible section showing tool calls (SQL queries).
 */
function ToolCallsSection({ toolCalls }: { toolCalls: ToolCall[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mt-2 w-full">
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
      >
        {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Database className="w-3.5 h-3.5" />
        <span>
          {toolCalls.length} tool{toolCalls.length !== 1 ? 's' : ''} used
        </span>
      </button>

      {/* Expanded Tool Calls */}
      {isExpanded && (
        <div className="mt-2 space-y-2">
          {toolCalls.map((tc, index) => (
            <ToolCallCard key={index} toolCall={tc} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Card displaying a single tool call with its query and result.
 */
function ToolCallCard({ toolCall }: { toolCall: ToolCall }) {
  const [showResult, setShowResult] = useState(false);

  // Check if this is an Excel export tool call
  const excelResult = useMemo(() => {
    if (toolCall.name === 'export_to_excel' && toolCall.result) {
      return parseExcelExportResult(toolCall.result);
    }
    return null;
  }, [toolCall.name, toolCall.result]);

  // If it's an Excel export, render the download card
  if (excelResult) {
    return <ExcelDownloadCard result={excelResult} />;
  }

  // Extract SQL query from arguments
  const query =
    typeof toolCall.arguments === 'object' && toolCall.arguments !== null
      ? (toolCall.arguments as Record<string, unknown>).sql ||
        (toolCall.arguments as Record<string, unknown>).query ||
        JSON.stringify(toolCall.arguments, null, 2)
      : String(toolCall.arguments);

  return (
    <div className="bg-surface-inset border border-border-subtle rounded-lg overflow-hidden text-xs">
      {/* Header */}
      <div className="px-3 py-2 bg-surface-raised border-b border-border-subtle flex items-center justify-between">
        <span className="font-medium text-text-primary">{toolCall.name}</span>
        {toolCall.result && (
          <button
            onClick={() => setShowResult(!showResult)}
            className="text-text-secondary hover:text-text-primary text-xs"
          >
            {showResult ? 'Hide result' : 'Show result'}
          </button>
        )}
      </div>

      {/* Query */}
      <div className="px-3 py-2">
        <pre className="text-text-secondary whitespace-pre-wrap font-mono text-xs overflow-x-auto">{String(query)}</pre>
      </div>

      {/* Result (if expanded) */}
      {showResult && toolCall.result && (
        <div className="px-3 py-2 bg-surface-base border-t border-border-subtle">
          <pre className="text-text-secondary whitespace-pre-wrap font-mono text-xs overflow-x-auto max-h-40 overflow-y-auto">
            {toolCall.result.length > 1000 ? toolCall.result.slice(0, 1000) + '...' : toolCall.result}
          </pre>
        </div>
      )}
    </div>
  );
}

/**
 * Format ISO timestamp to readable time.
 */
function formatTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * Parse Excel export result from tool call result string.
 */
function parseExcelExportResult(result: string): ExcelExportResult | null {
  try {
    const parsed = JSON.parse(result);
    if (typeof parsed === 'object' && parsed !== null && 'success' in parsed) {
      return parsed as ExcelExportResult;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Format file size in human-readable format.
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Card for displaying Excel export result with download button.
 */
function ExcelDownloadCard({ result }: { result: ExcelExportResult }) {
  if (!result.success) {
    return (
      <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-red-700 dark:text-red-400">
          <p className="font-medium">Export failed</p>
          <p className="text-xs mt-0.5">{result.error || result.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
      <div className="flex items-start gap-3">
        <div className="bg-green-100 dark:bg-green-900/40 rounded-lg p-2">
          <FileSpreadsheet className="w-5 h-5 text-green-600 dark:text-green-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-text-primary text-sm truncate">{result.filename}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-text-secondary mt-0.5">
            {result.row_count !== undefined && <span>{result.row_count.toLocaleString()} rows</span>}
            {result.column_count !== undefined && <span>{result.column_count} columns</span>}
            {result.file_size_bytes !== undefined && <span>{formatFileSize(result.file_size_bytes)}</span>}
          </div>
          {result.expires_in_seconds !== undefined && (
            <p className="text-xs text-amber-600 mt-1">
              ⏱ Expires in {Math.round(result.expires_in_seconds / 60)} minutes
            </p>
          )}
        </div>
        {result.download_url && (
          <Button variant="coral" size="sm" asChild className="flex-shrink-0">
            <a href={result.download_url} download>
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Download
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}
